/**
 * Canonical social-post model + per-platform Zapier payload contracts.
 *
 * WTC publishes to social platforms by POSTing a JSON payload to a
 * platform-specific Zapier "catch" webhook. Zapier does the actual posting.
 * This module is the strongly-typed seam between our canonical post model and
 * each webhook's exact contract, so that a shape mismatch is a compile error,
 * not a silent failed post.
 *
 * Nothing here reads secrets or performs network I/O — see `zapier-transport.ts`
 * / `buffer/client.ts` for transport, and the consuming app's own brand-config
 * layer for which webhook/channel serves which brand.
 */

import { z } from "zod";

/**
 * Every social platform WTC currently publishes to. Values are stable string
 * keys persisted in the database (`social_media_posts.platforms`) — do not
 * rename without a migration.
 */
export const SOCIAL_PLATFORMS = [
  "facebook",
  "twitter",
  "google_business",
  "instagram",
  "bluesky",
  "youtube",
  "linkedin",
  "tiktok",
] as const;

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export function isSocialPlatform(value: string): value is SocialPlatform {
  return (SOCIAL_PLATFORMS as readonly string[]).includes(value);
}

/**
 * Platforms fulfilled by the shared Buffer multi-channel Zap (one webhook,
 * routed by `channelId`). Instagram/Bluesky/YouTube each have their own Zap.
 */
export const BUFFER_PLATFORMS = [
  "facebook",
  "twitter",
  "google_business",
] as const;

export type BufferPlatform = (typeof BUFFER_PLATFORMS)[number];

export function isBufferPlatform(
  platform: SocialPlatform,
): platform is BufferPlatform {
  return (BUFFER_PLATFORMS as readonly string[]).includes(platform);
}

/**
 * TikTok's title limit on a photo post. Enforced by Buffer's API ("TikTok title cannot exceed 90
 * characters", confirmed against the live API 2026-10-05), so the validator mirrors it rather than
 * letting the failure surface as a `rejected` publish.
 */
export const TIKTOK_TITLE_MAX_LENGTH = 90;

/** Media attached to a post. `none` = text-only (valid for X/Bluesky/FB). */
export type SocialMediaKind = "none" | "image" | "video";

/**
 * YouTube video categories (Zapier `category` field takes the numeric id as a
 * string). Restricted to the categories WTC realistically publishes under.
 */
export const YOUTUBE_CATEGORIES = {
  film_animation: "1",
  autos_vehicles: "2",
  music: "10",
  pets_animals: "15",
  sports: "17",
  travel_events: "19",
  gaming: "20",
  people_blogs: "22",
  comedy: "23",
  entertainment: "24",
  news_politics: "25",
  howto_style: "26",
  education: "27",
  science_technology: "28",
  nonprofits_activism: "29",
} as const;

export type YoutubeCategoryKey = keyof typeof YOUTUBE_CATEGORIES;
export type YoutubeCategoryId = (typeof YOUTUBE_CATEGORIES)[YoutubeCategoryKey];

export const DEFAULT_YOUTUBE_CATEGORY: YoutubeCategoryId =
  YOUTUBE_CATEGORIES.science_technology;

export type YoutubeVisibility = "public" | "private" | "unlisted";

/**
 * Buffer's required `CreatePostInput.type` for Facebook, confirmed live
 * 2026-09-30 (see `buffer/adapter.ts` note 3 for the full flip-flop history).
 */
export const FACEBOOK_POST_TYPES = ["post", "story", "reel"] as const;
export type FacebookPostType = (typeof FACEBOOK_POST_TYPES)[number];

export function isFacebookPostType(value: unknown): value is FacebookPostType {
  return (
    typeof value === "string" &&
    (FACEBOOK_POST_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Buffer's required `CreatePostInput.type` for Google Business Profile,
 * confirmed live 2026-09-30 alongside the Facebook requirement above.
 */
export const GOOGLE_BUSINESS_POST_TYPES = ["event", "offer", "whats_new"] as const;
export type GoogleBusinessPostType = (typeof GOOGLE_BUSINESS_POST_TYPES)[number];

export function isGoogleBusinessPostType(
  value: unknown,
): value is GoogleBusinessPostType {
  return (
    typeof value === "string" &&
    (GOOGLE_BUSINESS_POST_TYPES as readonly string[]).includes(value)
  );
}

/**
 * The canonical, platform-agnostic description of a post's content. One of
 * these is authored by a user (web UI, Slack modal, or a chat tool) and
 * fanned out to each targeted platform's adapter. Fields not relevant to a
 * given platform are ignored by that platform's adapter.
 */
export interface SocialPostContent {
  /** Primary body / caption. Required for text platforms; caption for media. */
  readonly text: string;
  readonly mediaKind: SocialMediaKind;
  /** Public URL (social-media-assets bucket) of the image or video. */
  readonly mediaUrl?: string;
  /** Optional thumbnail (YouTube). */
  readonly thumbnailUrl?: string;
  /** Canonical hashtags/tags WITHOUT leading '#'. Adapters format per platform. */
  readonly tags: readonly string[];
  /** Optional human location string (Instagram). */
  readonly location?: string;
  /** Buffer's required post type for Facebook. Defaults to "post" when absent. */
  readonly facebookPostType?: FacebookPostType;
  /** Buffer's required post type for Google Business Profile. Defaults to "whats_new" when absent. */
  readonly googleBusinessPostType?: GoogleBusinessPostType;
  /**
   * TikTok-specific metadata (Buffer `TikTokPostMetadataInput`). Optional: `title` applies to photo
   * posts only; `isAiGenerated` discloses AI-generated content on a video.
   */
  readonly tiktok?: {
    readonly title?: string;
    readonly isAiGenerated?: boolean;
  };
  /** YouTube-specific metadata; required only when publishing to YouTube. */
  readonly youtube?: {
    readonly title: string;
    readonly description: string;
    readonly visibility: YoutubeVisibility;
    readonly categoryId: YoutubeCategoryId;
    readonly madeForKids: boolean;
    readonly language?: string;
    readonly recordingDate?: string; // ISO 8601
    readonly recordingLocation?: string;
    readonly notifySubscribers?: boolean;
  };
}

/**
 * Sparse per-platform caption overrides. `SocialPostContent.text` (from the
 * post's `body`) is the BASE caption fanned to every platform; a platform listed
 * here overrides ONLY its own caption (LinkedIn ≠ X ≠ Instagram voice/length).
 * Persisted verbatim in `social_media_posts.platform_overrides` (jsonb).
 */
/**
 * A `type` (not an `interface`) on purpose: object types satisfy the jsonb
 * column's string index signature, so PlatformOverrides is assignable to `Json`
 * without a cast. Interfaces don't get an implicit index signature.
 */
export type PlatformCaptionOverride = {
  /** Replacement caption for this platform. Empty/absent → use the base caption. */
  readonly text?: string;
  /**
   * Buffer's required post `type` for this platform (facebook/google_business
   * only — ignored for every other platform). See `FacebookPostType` /
   * `GoogleBusinessPostType`.
   */
  readonly postType?: FacebookPostType | GoogleBusinessPostType;
};

export const platformOverridesSchema = z
  .partialRecord(
    z.enum(SOCIAL_PLATFORMS),
    z.object({
      text: z.string().optional(),
      postType: z
        .enum([...FACEBOOK_POST_TYPES, ...GOOGLE_BUSINESS_POST_TYPES])
        .optional(),
    }),
  )
  .superRefine((overrides, ctx) => {
    for (const [platform, override] of Object.entries(overrides)) {
      if (!override.postType) continue;
      if (platform === "facebook" && !isFacebookPostType(override.postType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `facebook postType must be one of: ${FACEBOOK_POST_TYPES.join(", ")}.`,
          path: [platform, "postType"],
        });
      } else if (
        platform === "google_business" &&
        !isGoogleBusinessPostType(override.postType)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `google_business postType must be one of: ${GOOGLE_BUSINESS_POST_TYPES.join(", ")}.`,
          path: [platform, "postType"],
        });
      } else if (platform !== "facebook" && platform !== "google_business") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `postType is only meaningful for facebook/google_business, not ${platform}.`,
          path: [platform, "postType"],
        });
      }
    }
  });

/**
 * Sparse map — only customized platforms are present (Partial), so `{}` and
 * `{ linkedin: {...} }` are both valid. The schema validates the same shape; a
 * full `Record` output from it is assignable to this Partial.
 */
export type PlatformOverrides = Partial<
  Record<SocialPlatform, PlatformCaptionOverride>
>;

/**
 * Max caption length per platform (characters). Drives the composer's live
 * counter AND `validateContentForPlatform`, so the UI and the publish gate can
 * never disagree. Values are each network's documented post/caption limit.
 */
export const PLATFORM_CHAR_LIMITS: Record<SocialPlatform, number> = {
  twitter: 280,
  bluesky: 300,
  google_business: 1500,
  instagram: 2200,
  linkedin: 3000,
  facebook: 63206,
  youtube: 5000, // description; title is validated separately (100)
  tiktok: 2200, // video caption; photo posts allow more (4000) but 2200 is the safe common limit
} as const;

/**
 * Count user-perceived characters (grapheme clusters), not UTF-16 code units.
 *
 * `string.length` counts UTF-16 code units, so a single emoji — especially a
 * multi-codepoint one (skin-tone modifier, ZWJ family/couple sequences) —
 * can count as 2, 4, or more. Against a hard per-platform limit that silently
 * FALSE-REJECTS a caption a human would see as well under the limit. Every
 * modern JS runtime (Node 16+) ships `Intl.Segmenter`, which is the correct,
 * locale-aware way to do this — no dependency needed.
 */
export function graphemeLength(text: string): number {
  const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  let count = 0;
  for (const _ of segmenter.segment(text)) count += 1;
  return count;
}

/**
 * A TikTok photo-post title derived from a caption: its first non-empty line, cut to the 90-grapheme
 * limit (with an ellipsis when cut). TikTok photo posts show a title, and a marketer writing one
 * caption for several platforms has not written one for TikTok.
 */
export function deriveTiktokTitle(text: string): string {
  const firstLine =
    text
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line.length > 0) ?? "";
  if (graphemeLength(firstLine) <= TIKTOK_TITLE_MAX_LENGTH) return firstLine;
  const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  const graphemes = [...segmenter.segment(firstLine)].map((part) => part.segment);
  return graphemes.slice(0, TIKTOK_TITLE_MAX_LENGTH - 1).join("").trimEnd() + "\u2026";
}

/** Resolve the caption a given platform will actually publish (override → base). */
export function resolveCaptionForPlatform(
  platform: SocialPlatform,
  baseText: string,
  overrides: PlatformOverrides | null | undefined,
): string {
  const override = overrides?.[platform]?.text?.trim();
  return override && override.length > 0 ? override : baseText;
}

// ---------------------------------------------------------------------------
// Per-platform Zapier payloads (exact wire contracts)
// ---------------------------------------------------------------------------

/** Buffer multi-channel Zap (FB / X / Google Business). Tags MUST be an array. */
export interface BufferZapierPayload {
  readonly text: string;
  readonly attachmentType: SocialMediaKind;
  readonly imageUrl?: string;
  readonly videoUrl?: string;
  readonly tags: string[];
  readonly channelId: string;
}

/** Instagram Zap. Hashtags are a single space-separated `#tag` string. */
export interface InstagramZapierPayload {
  readonly caption: string;
  readonly hashtags: string;
  readonly location?: string;
  readonly image_url?: string;
  readonly video_url?: string;
  readonly type: "photo" | "video";
}

/**
 * Bluesky Zap. Zapier's own native Bluesky "Create Post" action DOES support a
 * real image attachment via an `Image` input field (confirmed 2026-09-30 against
 * Zapier's own docs/community posts — it takes an image URL, subject to
 * Bluesky's 1MB blob-size limit, and Zapier performs the AT Protocol blob upload
 * on our behalf; this is NOT the same as a raw webhook passthrough).
 *
 * `image_url` on this payload is only useful once whoever owns the Zap has
 * mapped this webhook field to that action's `Image` input in the Zap editor —
 * our code cannot verify or configure that wiring. See
 * `describeZapierMediaCaveat` for the caller-facing warning this implies.
 */
export interface BlueskyZapierPayload {
  readonly text: string;
  readonly image_url?: string;
}

/**
 * LinkedIn Zap. `raw_body` is the post text (required, ≤3000). When `image_url`
 * is present the Zap's Branch A runs (a link-preview card with the image +
 * optional title/description); when absent, Branch B posts text only.
 *
 * CONFIRMED PLATFORM LIMITATION (researched 2026-09-30, not just this repo's
 * own guess): Zapier's LinkedIn "Create Share Update" / "Create Company Update"
 * actions have a long-standing, acknowledged bug/limitation where supplying an
 * image forces the post into a link-preview card shape rather than a natively
 * embedded photo — Zapier's own community has reported this with no workaround
 * available. There is no alternate field or action on the LinkedIn integration
 * that produces a true embedded image through this transport. Do not spend
 * further effort trying to "fix" this in our payload shape — it is not fixable
 * from the caller side. The only real fix is migrating this platform off the
 * Zapier catch-hook onto Buffer (`buffer/adapter.ts`), which posts LinkedIn
 * images as real native attachments.
 */
export interface LinkedinZapierPayload {
  readonly raw_body: string;
  readonly image_url?: string;
  readonly preview_title?: string;
  readonly preview_description?: string;
}

/** YouTube Zap. All booleans + category are strings on the wire. */
export interface YoutubeZapierPayload {
  readonly title: string;
  readonly description: string;
  readonly video_url: string;
  readonly thumbnail_url?: string;
  readonly tags?: string; // comma-separated
  readonly visibility: YoutubeVisibility;
  readonly category: string; // numeric id as string
  readonly language?: string;
  readonly recording_date?: string;
  readonly recording_location?: string;
  readonly made_for_kids: "true" | "false";
  readonly notify_subscribers?: "true" | "false";
}

/**
 * Discriminated union of every payload we can send, tagged by `platform` for
 * exhaustive handling. `channelId` on the buffer variant is resolved from the
 * consuming app's own brand/platform registry at build time.
 */
export type PlatformDispatch =
  | { platform: BufferPlatform; webhookUrl: string; payload: BufferZapierPayload }
  | { platform: "instagram"; webhookUrl: string; payload: InstagramZapierPayload }
  | { platform: "bluesky"; webhookUrl: string; payload: BlueskyZapierPayload }
  | { platform: "youtube"; webhookUrl: string; payload: YoutubeZapierPayload }
  | { platform: "linkedin"; webhookUrl: string; payload: LinkedinZapierPayload };

// ---------------------------------------------------------------------------
// Zod validation of canonical content per targeted platform
// ---------------------------------------------------------------------------

const nonEmpty = (label: string) => z.string().trim().min(1, `${label} is required`);

const youtubeContentSchema = z.object({
  title: nonEmpty("YouTube title"),
  description: z.string(),
  visibility: z.enum(["public", "private", "unlisted"]),
  categoryId: z.string().regex(/^\d+$/, "YouTube category must be a numeric id"),
  madeForKids: z.boolean(),
  language: z.string().optional(),
  recordingDate: z.string().optional(),
  recordingLocation: z.string().optional(),
  notifySubscribers: z.boolean().optional(),
});

const tiktokContentSchema = z.object({
  title: z.string().max(TIKTOK_TITLE_MAX_LENGTH).optional(),
  isAiGenerated: z.boolean().optional(),
});

export const socialPostContentSchema = z.object({
  text: z.string(),
  mediaKind: z.enum(["none", "image", "video"]),
  mediaUrl: z.string().url().optional(),
  thumbnailUrl: z.string().url().optional(),
  tags: z.array(z.string()),
  location: z.string().optional(),
  tiktok: tiktokContentSchema.optional(),
  youtube: youtubeContentSchema.optional(),
});

/**
 * Per-platform requirements that go beyond the shared content schema (e.g.
 * YouTube requires a video + youtube block; Instagram requires media). Returns
 * a list of human-readable problems; empty = valid for that platform.
 */
export function validateContentForPlatform(
  platform: SocialPlatform,
  content: SocialPostContent,
): string[] {
  const problems: string[] = [];

  // Shared caption-length gate (mirrors the composer's live counter). YouTube's
  // published text is its title/description block, not `text`, so it is checked
  // in its own case below rather than against the generic caption limit.
  //
  // Counted in graphemes, not `.length` (UTF-16 code units) — an emoji-heavy
  // caption that is genuinely under a platform's real (human-perceived)
  // character limit must not be rejected just because it renders as more UTF-16
  // units. See `graphemeLength`.
  if (platform !== "youtube" && graphemeLength(content.text) > PLATFORM_CHAR_LIMITS[platform]) {
    problems.push(
      `${platform}: caption exceeds the ${PLATFORM_CHAR_LIMITS[platform]}-character limit.`,
    );
  }

  switch (platform) {
    case "youtube": {
      if (content.mediaKind !== "video" || !content.mediaUrl) {
        problems.push("YouTube requires a video (mediaKind='video' + mediaUrl).");
      }
      if (!content.youtube) {
        problems.push("YouTube requires the youtube metadata block.");
      } else {
        const parsed = youtubeContentSchema.safeParse(content.youtube);
        if (!parsed.success) {
          problems.push(
            ...parsed.error.issues.map((i) => `YouTube: ${i.message}`),
          );
        }
      }
      break;
    }
    case "instagram": {
      if (content.mediaKind === "none" || !content.mediaUrl) {
        problems.push("Instagram requires an image or video.");
      }
      if (!content.text.trim()) {
        problems.push("Instagram requires a caption.");
      }
      break;
    }
    case "bluesky": {
      if (!content.text.trim()) {
        problems.push("Bluesky requires text.");
      }
      break;
    }
    case "linkedin": {
      if (!content.text.trim()) {
        problems.push("LinkedIn requires text (raw_body).");
      }
      // Length is enforced by the shared caption-length gate above.
      // Image is optional (Branch A vs B); LinkedIn's webhook takes an image
      // link only — a video mediaUrl is not sent (post falls back to text).
      if (content.mediaKind === "image" && !content.mediaUrl) {
        problems.push("LinkedIn: image selected but mediaUrl missing.");
      }
      break;
    }
    case "tiktok": {
      // TikTok has no text-only post: every post is a video or a photo.
      if (content.mediaKind === "none" || !content.mediaUrl) {
        problems.push("TikTok requires a video or an image.");
      }
      if (
        content.tiktok?.title !== undefined &&
        graphemeLength(content.tiktok.title) > TIKTOK_TITLE_MAX_LENGTH
      ) {
        problems.push(
          `tiktok: title exceeds the ${TIKTOK_TITLE_MAX_LENGTH}-character limit.`,
        );
      }
      break;
    }
    case "facebook": {
      if (!content.text.trim() && content.mediaKind === "none") {
        problems.push(`${platform}: a post needs text or media.`);
      }
      if (content.mediaKind !== "none" && !content.mediaUrl) {
        problems.push(`${platform}: mediaKind set but mediaUrl missing.`);
      }
      if (
        content.facebookPostType !== undefined &&
        !isFacebookPostType(content.facebookPostType)
      ) {
        problems.push(
          `facebook: postType must be one of: ${FACEBOOK_POST_TYPES.join(", ")}.`,
        );
      }
      break;
    }
    case "google_business": {
      if (!content.text.trim() && content.mediaKind === "none") {
        problems.push(`${platform}: a post needs text or media.`);
      }
      if (content.mediaKind !== "none" && !content.mediaUrl) {
        problems.push(`${platform}: mediaKind set but mediaUrl missing.`);
      }
      if (
        content.googleBusinessPostType !== undefined &&
        !isGoogleBusinessPostType(content.googleBusinessPostType)
      ) {
        problems.push(
          `google_business: postType must be one of: ${GOOGLE_BUSINESS_POST_TYPES.join(", ")}.`,
        );
      }
      break;
    }
    case "twitter": {
      if (!content.text.trim() && content.mediaKind === "none") {
        problems.push(`${platform}: a post needs text or media.`);
      }
      if (content.mediaKind !== "none" && !content.mediaUrl) {
        problems.push(`${platform}: mediaKind set but mediaUrl missing.`);
      }
      break;
    }
    default: {
      // Exhaustiveness guard — a new platform must handle validation here.
      const _exhaustive: never = platform;
      return _exhaustive;
    }
  }

  return problems;
}
